// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2023 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import PreviouslyPurchasedBadge from "@guestsupply/App/Components/CustomBadges/PreviouslyPurchasedBadge";
import { getSettingsCollection } from "@guestsupply/App/Store/Selectors/SettingsSelectors";
import { mergeWithCss } from "@guestsupply/XDL/Utilities";
import useAppSelector from "@insite/client-framework/Common/Hooks/useAppSelector";
import mergeToNew from "@insite/client-framework/Common/mergeToNew";
import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import parseQueryString from "@insite/client-framework/Common/Utilities/parseQueryString";
import { ProductContextModel } from "@insite/client-framework/Components/ProductContext";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getLocation } from "@insite/client-framework/Store/Data/Pages/PageSelectors";
import translate from "@insite/client-framework/Translate";
import { BadgeTypeValues, CartLineModel } from "@insite/client-framework/Types/ApiModels";
import Badge from "@insite/mobius/Badge";
import Clickable, { ClickablePresentationProps } from "@insite/mobius/Clickable";
import Img, { ImgProps } from "@insite/mobius/Img";
import { LazyImageProps } from "@insite/mobius/LazyImage";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import React, { useState } from "react";
import { connect } from "react-redux";
import { css } from "styled-components";

interface OwnProps {
    product: CartLineModel | ProductContextModel;
    onClick?: React.EventHandler<React.MouseEvent>;
    extendedStyles?: ProductImageStyles;
    showSmallImage?: boolean;
    // XNG-Changes:
    showPreviouslyPurchasedTag?: boolean;
    isGridView?: boolean;
}

const mapStateToProps = (state: ApplicationState) => {
    const location = getLocation(state);
    const parsedQuery = parseQueryString<{ id?: string }>(location.search);
    const id = parsedQuery.id;
    return {
        orderFormId: state.context.session.properties?.orderFormId || "",
        wishListId: id,
    };
};

type Props = OwnProps & ReturnType<typeof mapStateToProps>;

export interface ProductImageStyles {
    linkWrappingImage?: ClickablePresentationProps;
    /**
     * @deprecated Use img instead
     */
    image?: LazyImageProps;
    img?: ImgProps;
    // XNG-Changes
    textBadge?: InjectableCss;
    badgeWrapper?: InjectableCss;
}

// Xng-Changes: please check ProductImageExtensions.ts for styles
export const productImageStyles: ProductImageStyles = {};

const ProductImage = ({
    product,
    onClick,
    extendedStyles,
    showSmallImage,
    //  XNG-CHANGES
    showPreviouslyPurchasedTag = true,
    isGridView = true,
    orderFormId,
    wishListId,
}: Props) => {
    const [styles] = useState(() => mergeWithCss(productImageStyles, extendedStyles));

    let altText = "product" in product ? product.product.imageAltText : product.altText;
    if (!altText || altText === "") {
        altText = "product" in product ? product.product.productTitle : product.productName;
    }
    // XNG-CHANGES
    const { notFoundImage } = useAppSelector(state => {
        const { notFoundSmallImagePath, notFoundMediumImagePath, notFoundLargeImagePath } =
            getSettingsCollection(state).customSettings;
        return {
            notFoundImage: showSmallImage
                ? notFoundSmallImagePath || notFoundMediumImagePath
                : notFoundMediumImagePath || notFoundLargeImagePath,
        };
    });
    const imagePath =
        "product" in product
            ? showSmallImage
                ? product.product.smallImagePath || product.product.mediumImagePath
                : product.product.mediumImagePath || product.product.smallImagePath
            : product.smallImagePath;

    let productDetailPath = "product" in product ? product.productInfo.productDetailPath : product.productUri;
    const loading = product.idx === undefined ? "lazy" : product.idx < 3 ? "eager" : "lazy";

    if (orderFormId === wishListId) {
        productDetailPath += "?MyOrderGuidePage=true";
    }

    return (
        <Clickable
            {...styles.linkWrappingImage}
            href={productDetailPath}
            onClick={onClick}
            data-test-selector="productImage"
            aria-label="Product Image Link"
            {...{ isGridView }}
        >
            <Img
                {...styles.img}
                loading={loading}
                width="209"
                height="209"
                src={imagePath || notFoundImage}
                altText={altText}
            />
            {/* XNG-CHANGES */}
            {showPreviouslyPurchasedTag && (
                <StyledWrapper {...styles.badgeWrapper} className="PreviouslyPurchasedBadge" {...{ isGridView }}>
                    <PreviouslyPurchasedBadge product={product} {...styles.textBadge} />
                </StyledWrapper>
            )}
        </Clickable>
    );
};

export default connect(mapStateToProps)(ProductImage);
